import { useTranslation } from '@rhino-project/core/i18n';
import { useCallback, useMemo, useState } from 'react';

import {
  useBaseOwnerId,
  useModelIndexContext
} from '@rhino-project/core/hooks';
import { useResendInvitationAction } from '@rhino-project/core/queries';
import { getModuleInfo } from '@rhino-project/core/utils';

import { AlertDialog, Button, Chip, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { ModelEditableCellReference } from '../models/cells/ModelEditableCellReference';
import { ModelIndexHeader } from '../models/ModelIndexHeader';
import { ModelIndexTable } from '../models/ModelIndexTable';
import { IconButton } from '../buttons';
import { ModelCreateModal } from '../models/ModelCreateModal';
import { ModelIndexActions } from '../models/ModelIndexActions';
import { ModelIndexSimple } from '../models/ModelIndexSimple';

interface AccessRow {
  original: { id: number; user?: { email?: string } };
}

const RemoveButton = ({ row }: { row?: AccessRow }) => {
  const { t } = useTranslation();
  const {
    delete: { mutate, isPending }
  } = useModelIndexContext();
  const [isOpen, setIsOpen] = useState(false);

  const email = row?.original.user?.email;

  const handleRemove = useCallback(() => {
    if (!row) return;

    mutate(row.original.id, { onSuccess: () => setIsOpen(false) });
  }, [row, mutate]);

  return (
    <AlertDialog isOpen={isOpen} onOpenChange={setIsOpen}>
      <Button variant="danger-soft">
        <Icon icon="bi:trash" />
        {t('rhino.frontend.messages.remove_access')}
      </Button>
      <AlertDialog.Backdrop>
        <AlertDialog.Container size="sm">
          <AlertDialog.Dialog>
            <AlertDialog.CloseTrigger />
            <AlertDialog.Header>
              <AlertDialog.Icon status="danger" />
              <AlertDialog.Heading>
                {t('rhino.frontend.messages.remove_access')}
              </AlertDialog.Heading>
            </AlertDialog.Header>
            <AlertDialog.Body>
              {t('rhino.frontend.messages.remove_access_confirmation', {
                email: email || t('rhino.frontend.messages.this_user')
              })}
            </AlertDialog.Body>
            <AlertDialog.Footer>
              <Button variant="ghost" onPress={() => setIsOpen(false)}>
                {t('rhino.frontend.messages.cancel')}
              </Button>
              <Button
                variant="danger"
                onPress={handleRemove}
                isDisabled={isPending}
              >
                {isPending
                  ? t('rhino.frontend.messages.removing')
                  : t('rhino.frontend.messages.remove_access')}
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    </AlertDialog>
  );
};

const cellPaths = [
  'user.email',
  'user.name',
  <ModelEditableCellReference key="role" id="role" path="role" />,
  <RemoveButton key="remove" />
];

const sortPaths = ['user.email', 'user.name'];

const InviteStatus = ({
  row
}: {
  row?: { original: { updated_at: string } };
}) => {
  const { t } = useTranslation();
  const inviteFor = getModuleInfo('rhino')?.invite_for;
  const updatedAt = row?.original.updated_at;

  const expired = useMemo(() => {
    if (!updatedAt || !inviteFor) return false;
    return Date.parse(updatedAt) + inviteFor * 1000 < new Date().getTime();
  }, [updatedAt, inviteFor]);

  if (!row || !inviteFor) return null;

  return expired ? (
    <Chip color="danger">{t('rhino.frontend.messages.expired')}</Chip>
  ) : (
    <Chip>{t('rhino.frontend.messages.pending')}</Chip>
  );
};

const ResendInviteButton = ({
  row
}: {
  row?: { original: { id: number } };
}) => {
  const { t } = useTranslation();
  // Resend lives inside the pending-invites index, so refetch that query on
  // success to refresh the updated_at-derived status chip.
  const { refetch } = useModelIndexContext();
  const { mutate, isPending } = useResendInvitationAction();
  if (!row) return null;

  return (
    <Button
      variant="ghost"
      isDisabled={isPending}
      onPress={() => mutate(row.original.id, { onSuccess: () => refetch() })}
    >
      {t('rhino.frontend.messages.resend')}
    </Button>
  );
};

const RevokeInviteButton = ({
  row
}: {
  row?: { original: { id: number; email?: string } };
}) => {
  const { t } = useTranslation();
  const {
    delete: { mutate, isPending }
  } = useModelIndexContext();
  if (!row) return null;

  return (
    <Button
      variant="danger-soft"
      isDisabled={isPending}
      onPress={() => mutate(row.original.id)}
    >
      {t('rhino.frontend.messages.revoke')}
    </Button>
  );
};

const pendingPaths = [
  'email',
  <ModelEditableCellReference key="role" id="role" path="role" />,
  <InviteStatus key="status" />,
  <ResendInviteButton key="resend" />,
  <RevokeInviteButton key="revoke" />
];

const PendingInvites = () => {
  const { t } = useTranslation();
  return (
    <ModelIndexSimple model="users_role_invite" defaultOrder={['email']}>
      <div className="mt-8 flex flex-col gap-3">
        <Typography.Heading level={3}>
          {t('rhino.frontend.messages.pending_invites')}
        </Typography.Heading>
        <ModelIndexTable paths={pendingPaths} onRowClick={null} />
      </div>
    </ModelIndexSimple>
  );
};

export const EditOrganizationAccess = () => {
  const { t } = useTranslation();
  const baseOwnerId = useBaseOwnerId();
  const [modalOpen, setModalOpen] = useState(false);

  const handleAction = useCallback(() => setModalOpen(true), [setModalOpen]);
  const handleModalClose = () => setModalOpen(false);

  const actions = useMemo(() => {
    return [
      <IconButton
        key="invite"
        color="primary"
        icon="bi:plus"
        onPress={handleAction}
      >
        {t('rhino.frontend.messages.invite_user')}
      </IconButton>
    ];
  }, [handleAction, t]);

  return (
    <>
      <ModelIndexSimple model="users_role" defaultOrder={['user.email']}>
        <div className="flex flex-col gap-3">
          <ModelIndexHeader />
          <hr />
          <ModelIndexActions actions={actions} />
          <ModelIndexTable
            paths={cellPaths}
            sortPaths={sortPaths}
            onRowClick={null}
          />
        </div>
      </ModelIndexSimple>
      <PendingInvites />
      <ModelCreateModal
        model="users_role_invite"
        parentId={baseOwnerId}
        isOpen={modalOpen}
        onModalClose={handleModalClose}
      />
    </>
  );
};
